import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@org/ui';
import { Heart, MessageCircle, Pin, Lock, MoreHorizontal, Flag } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useEffect, useState } from 'react';
import { extractPlainText } from '@org/utils-client';
import type { RichTextDoc } from '@org/model';
import { ReportModal } from './report-modal';
import { ShareMenu } from './share-menu';

interface Post {
  postId: string;
  spaceId: string;
  authorId: string;
  type: 'discussion' | 'poll' | 'announcement';
  title: string;
  content: RichTextDoc;
  imageUrl?: string | null;
  tags?: string[] | null;
  isPinned: boolean;
  isLocked: boolean;
  likesCount: number;
  commentsCount: number;
  createdAt: Date;
  authorFirstName?: string | null;
  authorLastName?: string | null;
  authorDisplayName?: string | null;
  authorProfileImageUrl?: string | null;
  authorPoints?: number | null;
  spaceName?: string | null;
  spaceSlug?: string | null;
}

interface PostCardProps {
  post: Post;
}

export function PostCard({ post }: PostCardProps) {
  const { t } = useTranslation('community');
  const navigate = useNavigate();
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likesCount);
  const [showReportModal, setShowReportModal] = useState(false);

  const utils = communityTrpc.useUtils();

  const { data: likedData } = communityTrpc.likes.hasLiked.useQuery({
    targetType: 'post',
    targetId: post.postId,
  });

  useEffect(() => {
    if (likedData) setIsLiked(likedData.hasLiked);
  }, [likedData?.hasLiked]);

  useEffect(() => {
    setLikesCount(post.likesCount);
  }, [post.likesCount]);

  const likeMutation = communityTrpc.likes.toggle.useMutation({
    onMutate: async () => {
      // Optimistic update
      setIsLiked((v) => !v);
      setLikesCount((prev) => (isLiked ? prev - 1 : prev + 1));
    },
    onError: () => {
      // Revert on error
      setIsLiked((v) => !v);
      setLikesCount(post.likesCount);
    },
    onSettled: () => {
      utils.posts.getFeed.invalidate();
      utils.likes.hasLiked.invalidate({ targetType: 'post', targetId: post.postId });
    },
  });

  const authorName = post.authorDisplayName ||
    [post.authorFirstName, post.authorLastName].filter(Boolean).join(' ') ||
    'Anonymous';
  const initial = authorName.slice(0, 1).toUpperCase();

  const handleLike = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    likeMutation.mutate({ targetType: 'post', targetId: post.postId });
  };

  const goToAuthorProfile = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate({ to: '/profile/$userId', params: { userId: post.authorId } });
  };

  const authorClickProps = {
    role: 'link' as const,
    tabIndex: 0,
    onClick: goToAuthorProfile,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') goToAuthorProfile(e);
    },
  };

  const plainContent = extractPlainText(post.content);
  const truncatedContent = plainContent.length > 200
    ? plainContent.slice(0, 200) + '...'
    : plainContent;
  const shareText = plainContent.length > 100
    ? plainContent.slice(0, 100) + '...'
    : plainContent;

  return (
    <Link to={`/posts/${post.postId}`}>
      <article className="rounded-2xl border border-border bg-card p-4 transition-shadow hover:shadow-md">
        {/* Header */}
        <div className="flex items-start gap-3">
          <span {...authorClickProps} aria-label={authorName} className="cursor-pointer">
            <Avatar className="h-10 w-10">
              <AvatarImage src={post.authorProfileImageUrl ?? undefined} />
              <AvatarFallback className="bg-primary/10 text-primary">
                {initial}
              </AvatarFallback>
            </Avatar>
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span {...authorClickProps} className="font-medium truncate cursor-pointer hover:underline">{authorName}</span>
              {post.authorPoints !== null && post.authorPoints !== undefined && (
                <span className="flex-shrink-0 text-xs text-primary">
                  {post.authorPoints} pts
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {post.spaceName && (
                <>
                  <span className="truncate">{post.spaceName}</span>
                  <span>·</span>
                </>
              )}
              <span className="flex-shrink-0">
                {formatDistanceToNow(new Date(post.createdAt), { addSuffix: true })}
              </span>
            </div>
          </div>

          {/* Badges */}
          <div className="flex items-center gap-1">
            {post.isPinned && (
              <span className="rounded-full bg-primary/10 p-1" role="img" aria-label={t('postDetail.pinned')}>
                <Pin className="h-3 w-3 text-primary" aria-hidden="true" />
              </span>
            )}
            {post.isLocked && (
              <span className="rounded-full bg-muted p-1" role="img" aria-label={t('postDetail.locked')}>
                <Lock className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
              </span>
            )}
            {post.type === 'announcement' && (
              <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-[10px] font-medium text-yellow-800">
                {t('postDetail.announcement')}
              </span>
            )}
            {post.type === 'poll' && (
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-800">
                {t('postDetail.poll')}
              </span>
            )}

            {/* More Options */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => e.preventDefault()}
                  aria-label={t('postDetail.moreOptions')}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="cursor-pointer text-amber-600 focus:text-amber-600"
                  onSelect={(e) => {
                    e.preventDefault();
                    setShowReportModal(true);
                  }}
                >
                  <Flag className="mr-2 h-4 w-4" />
                  {t('postDetail.reportPost')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Content */}
        <div className="mt-3">
          <h3 className="font-semibold">{post.title}</h3>
          <p className="mt-1 line-clamp-3 text-sm text-muted-foreground">{truncatedContent}</p>
        </div>

        {/* Image */}
        {post.imageUrl && (
          <div className="mt-3">
            <img
              src={post.imageUrl}
              alt=""
              className="rounded-lg max-h-64 w-full object-cover"
            />
          </div>
        )}

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {post.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 flex items-center gap-4 border-t pt-3">
          <Button
            variant="ghost"
            size="sm"
            className={`gap-1.5 ${isLiked ? 'text-red-500' : ''}`}
            onClick={handleLike}
            aria-label={t('postDetail.likeCount', { count: likesCount })}
            aria-pressed={isLiked}
          >
            <Heart className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} aria-hidden="true" />
            <span>{likesCount}</span>
          </Button>
          <Button variant="ghost" size="sm" className="gap-1.5" aria-label={t('postDetail.commentCount', { count: post.commentsCount })}>
            <MessageCircle className="h-4 w-4" aria-hidden="true" />
            <span>{post.commentsCount}</span>
          </Button>
          <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
            <ShareMenu
              url={`${window.location.origin}/posts/${post.postId}`}
              title={post.title}
              text={shareText}
            />
          </div>
        </div>
      </article>

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        targetType="post"
        targetId={post.postId}
        targetName={post.title}
      />
    </Link>
  );
}
